import { useTranslation } from "react-i18next"

import type {
  HostedAgentEvalCaseDraft,
  HostedAgentEvalCaseErrorCode,
  HostedAgentEvalCaseValidation,
  HostedAgentEvalExpectationDraft,
  HostedAgentEvalExpectationErrorCode,
} from "../utils/hosted-agent-eval-cases"
import type { HostedAgentEvalExpectationKind } from "@/api/admin-api"

import {
  HOSTED_AGENT_EVAL_EXPECTATION_KINDS,
  HOSTED_AGENT_EVAL_TOOL_DENIAL_CODES,
  MAX_EVAL_CASE_NAME_LENGTH,
  MAX_EVAL_CASE_PROMPT_LENGTH,
  MAX_EVAL_EXPECTATIONS_PER_CASE,
} from "@/api/admin-api"
import { Plus, Trash2 } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { Textarea } from "@/components/ui/textarea"

import { HostedAgentEvalKindLabel } from "./hosted-agent-eval-outcome-badge"
import { createBlankHostedAgentEvalExpectation } from "../utils/hosted-agent-eval-cases"
import {
  HOSTED_AGENT_EVAL_RUN_STATUS_VALUES,
  hostedAgentEvalExpectationFields,
} from "../utils/hosted-agent-evals"

// Editor for one eval case (#3028).
//
// Two constraints shape this form. The server model is `extra="forbid"`, so
// the editor only ever shows — and only ever sends — the fields the chosen
// kind actually uses. And an expectation must be readable by an admin who does
// not know the contract by heart, so every kind gets a plain-language name and
// a one-line explanation of what it checks.

type CaseEditorProps = {
  draft: HostedAgentEvalCaseDraft
  validation: HostedAgentEvalCaseValidation
  showErrors: boolean
  onChange: (changes: Partial<HostedAgentEvalCaseDraft>) => void
}

export function HostedAgentEvalCaseEditor({
  draft,
  validation,
  showErrors,
  onChange,
}: CaseEditorProps) {
  const { t } = useTranslation()
  const errors = showErrors ? validation : { case: [], expectations: {} }
  const inputId = draft.id ?? "new"

  const updateExpectation = (key: string, changes: Partial<HostedAgentEvalExpectationDraft>) => {
    onChange({
      expectations: draft.expectations.map((expectation) =>
        expectation.key === key ? { ...expectation, ...changes } : expectation
      ),
    })
  }

  const removeExpectation = (key: string) => {
    onChange({ expectations: draft.expectations.filter((item) => item.key !== key) })
  }

  const addExpectation = () => {
    onChange({ expectations: [...draft.expectations, createBlankHostedAgentEvalExpectation()] })
  }

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor={`eval-case-name-${inputId}`}>
            {t("adminDashboard.hostedAgents.evals.editor.name", { defaultValue: "Case name" })}
          </Label>
          <Input
            id={`eval-case-name-${inputId}`}
            maxLength={MAX_EVAL_CASE_NAME_LENGTH}
            value={draft.name}
            onChange={(event) => onChange({ name: event.target.value })}
          />
          <CaseError codes={errors.case} match={["name_required", "name_too_long"]} />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`eval-case-timeout-${inputId}`}>
            {t("adminDashboard.hostedAgents.evals.editor.timeout", {
              defaultValue: "Timeout (seconds)",
            })}
          </Label>
          <Input
            className="w-32"
            id={`eval-case-timeout-${inputId}`}
            inputMode="numeric"
            value={draft.timeoutSeconds}
            onChange={(event) => onChange({ timeoutSeconds: event.target.value })}
          />
          <CaseError codes={errors.case} match={["timeout_invalid"]} />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor={`eval-case-prompt-${inputId}`}>
          {t("adminDashboard.hostedAgents.evals.editor.prompt", { defaultValue: "Prompt" })}
        </Label>
        <Textarea
          id={`eval-case-prompt-${inputId}`}
          maxLength={MAX_EVAL_CASE_PROMPT_LENGTH}
          rows={4}
          value={draft.prompt}
          onChange={(event) => onChange({ prompt: event.target.value })}
        />
        <p className="text-muted-foreground text-xs">
          {t("adminDashboard.hostedAgents.evals.editor.promptHelp", {
            defaultValue:
              "The exact message the agent receives. Keep it fixed — changing it changes the test.",
          })}
        </p>
        <CaseError codes={errors.case} match={["prompt_required", "prompt_too_long"]} />
      </div>

      <div className="flex items-center gap-2">
        <Switch
          checked={draft.enabled}
          id={`eval-case-enabled-${inputId}`}
          onCheckedChange={(enabled) => onChange({ enabled })}
        />
        <Label htmlFor={`eval-case-enabled-${inputId}`}>
          {t("adminDashboard.hostedAgents.evals.editor.enabled", {
            defaultValue: "Include this case when the suite runs",
          })}
        </Label>
      </div>

      <div className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Label>
            {t("adminDashboard.hostedAgents.evals.editor.expectations", {
              defaultValue: "Expectations",
            })}
          </Label>
          <Button
            disabled={draft.expectations.length >= MAX_EVAL_EXPECTATIONS_PER_CASE}
            size="sm"
            type="button"
            variant="outline"
            onClick={addExpectation}
          >
            <Plus className="size-4" />
            {t("adminDashboard.hostedAgents.evals.editor.addExpectation", {
              defaultValue: "Add expectation",
            })}
          </Button>
        </div>
        <p className="text-muted-foreground text-xs">
          {t("adminDashboard.hostedAgents.evals.editor.expectationsHelp", {
            defaultValue:
              "Every expectation must hold for the case to pass. If one of them cannot be observed, the case is reported as not measured — never as a pass.",
          })}
        </p>
        <CaseError codes={errors.case} match={["expectations_required", "expectations_too_many"]} />

        <ul className="space-y-2">
          {draft.expectations.map((expectation, index) => (
            <li key={expectation.key}>
              <ExpectationEditor
                canRemove={draft.expectations.length > 1}
                error={errors.expectations[expectation.key]}
                expectation={expectation}
                index={index}
                onChange={(changes) => updateExpectation(expectation.key, changes)}
                onRemove={() => removeExpectation(expectation.key)}
              />
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

function ExpectationEditor({
  expectation,
  index,
  error,
  canRemove,
  onChange,
  onRemove,
}: {
  expectation: HostedAgentEvalExpectationDraft
  index: number
  error: HostedAgentEvalExpectationErrorCode | undefined
  canRemove: boolean
  onChange: (changes: Partial<HostedAgentEvalExpectationDraft>) => void
  onRemove: () => void
}) {
  const { t } = useTranslation()
  const fields = hostedAgentEvalExpectationFields(expectation.kind)
  const idBase = `eval-expectation-${expectation.key}`

  return (
    <div className="space-y-3 rounded-lg border p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-48 flex-1 space-y-2">
          <Label htmlFor={`${idBase}-kind`}>
            {t("adminDashboard.hostedAgents.evals.editor.kind", {
              defaultValue: "Expectation {{number}}",
              number: index + 1,
            })}
          </Label>
          <Select
            value={expectation.kind}
            onValueChange={(kind) => onChange({ kind: kind as HostedAgentEvalExpectationKind })}
          >
            <SelectTrigger id={`${idBase}-kind`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {HOSTED_AGENT_EVAL_EXPECTATION_KINDS.map((kind) => (
                <SelectItem key={kind} value={kind}>
                  <HostedAgentEvalKindLabel kind={kind} />
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button
          className="mt-7"
          disabled={!canRemove}
          size="icon"
          type="button"
          variant="ghost"
          aria-label={t("adminDashboard.hostedAgents.evals.editor.removeExpectation", {
            defaultValue: "Remove expectation",
          })}
          onClick={onRemove}
        >
          <Trash2 className="size-4" />
        </Button>
      </div>

      <p className="text-muted-foreground text-xs">
        {t(`adminDashboard.hostedAgents.evals.kindHelp.${expectation.kind}`, {
          defaultValue: KIND_HELP_DEFAULTS[expectation.kind] ?? "",
        })}
      </p>

      {fields.expected ? (
        <div className="space-y-2">
          <Label htmlFor={`${idBase}-expected`}>
            {t("adminDashboard.hostedAgents.evals.editor.expected", {
              defaultValue: "Required status",
            })}
          </Label>
          <Select
            value={expectation.expected || "completed"}
            onValueChange={(expected) => onChange({ expected })}
          >
            <SelectTrigger className="w-48" id={`${idBase}-expected`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {HOSTED_AGENT_EVAL_RUN_STATUS_VALUES.map((status) => (
                <SelectItem key={status} value={status}>
                  {t(`adminDashboard.hostedAgents.evals.runStatus.${status}`, {
                    defaultValue: status,
                  })}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : null}

      {fields.value ? (
        <div className="space-y-2">
          <Label htmlFor={`${idBase}-value`}>
            {t("adminDashboard.hostedAgents.evals.editor.value", { defaultValue: "Text" })}
          </Label>
          <Input
            id={`${idBase}-value`}
            value={expectation.value}
            onChange={(event) => onChange({ value: event.target.value })}
          />
          <div className="flex items-center gap-2">
            <Checkbox
              checked={expectation.caseSensitive}
              id={`${idBase}-case`}
              onCheckedChange={(checked) => onChange({ caseSensitive: checked === true })}
            />
            <Label className="font-normal" htmlFor={`${idBase}-case`}>
              {t("adminDashboard.hostedAgents.evals.editor.caseSensitive", {
                defaultValue: "Match upper/lower case exactly",
              })}
            </Label>
          </div>
        </div>
      ) : null}

      {fields.tool ? (
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor={`${idBase}-tool`}>
              {t("adminDashboard.hostedAgents.evals.editor.tool", { defaultValue: "Tool name" })}
            </Label>
            <Input
              id={`${idBase}-tool`}
              value={expectation.tool}
              onChange={(event) => onChange({ tool: event.target.value })}
            />
            {fields.detailContains ? (
              <p className="text-muted-foreground text-xs">
                {t("adminDashboard.hostedAgents.evals.editor.toolHelp", {
                  defaultValue:
                    "Tool name. Alternatives can be joined with | (e.g. run_python|run_shell).",
                })}
              </p>
            ) : null}
          </div>
          {fields.action ? (
            <div className="space-y-2">
              <Label htmlFor={`${idBase}-action`}>
                {t("adminDashboard.hostedAgents.evals.editor.action", {
                  defaultValue: "Action (optional)",
                })}
              </Label>
              <Input
                id={`${idBase}-action`}
                value={expectation.action}
                onChange={(event) => onChange({ action: event.target.value })}
              />
            </div>
          ) : null}
          {fields.detailContains ? (
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor={`${idBase}-detail`}>
                {t("adminDashboard.hostedAgents.evals.editor.detailContains", {
                  defaultValue: "Arguments contain (optional)",
                })}
              </Label>
              <Input
                id={`${idBase}-detail`}
                placeholder="render_report.py"
                value={expectation.detailContains}
                onChange={(event) => onChange({ detailContains: event.target.value })}
              />
              <p className="text-muted-foreground text-xs">
                {t("adminDashboard.hostedAgents.evals.editor.detailContainsHelp", {
                  defaultValue:
                    "Matches the call's script path, command or file path — use it to require that a skill's own script actually ran.",
                })}
              </p>
            </div>
          ) : null}
          {fields.code ? (
            <div className="space-y-2">
              <Label htmlFor={`${idBase}-code`}>
                {t("adminDashboard.hostedAgents.evals.editor.code", {
                  defaultValue: "Denial reason (optional)",
                })}
              </Label>
              <Select
                value={expectation.code || "any"}
                onValueChange={(code) => onChange({ code: code === "any" ? "" : code })}
              >
                <SelectTrigger id={`${idBase}-code`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">
                    {t("adminDashboard.hostedAgents.evals.editor.codeAny", {
                      defaultValue: "Any denial reason",
                    })}
                  </SelectItem>
                  {HOSTED_AGENT_EVAL_TOOL_DENIAL_CODES.map((code) => (
                    <SelectItem key={code} value={code}>
                      {t(`adminDashboard.hostedAgents.evals.denialCodes.${code}`, {
                        defaultValue: code,
                      })}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}
        </div>
      ) : null}

      {fields.minCount ? (
        <div className="space-y-2">
          <Label htmlFor={`${idBase}-min-count`}>
            {t("adminDashboard.hostedAgents.evals.editor.minCount", {
              defaultValue: "At least how many",
            })}
          </Label>
          <Input
            className="w-32"
            id={`${idBase}-min-count`}
            inputMode="numeric"
            value={expectation.minCount}
            onChange={(event) => onChange({ minCount: event.target.value })}
          />
        </div>
      ) : null}

      {fields.limit ? (
        <div className="space-y-2">
          <Label htmlFor={`${idBase}-limit`}>
            {t(`adminDashboard.hostedAgents.evals.editor.limit.${expectation.kind}`, {
              defaultValue: LIMIT_LABEL_DEFAULTS[expectation.kind] ?? "Upper limit",
            })}
          </Label>
          <Input
            className="w-40"
            id={`${idBase}-limit`}
            inputMode="decimal"
            value={expectation.limit}
            onChange={(event) => onChange({ limit: event.target.value })}
          />
        </div>
      ) : null}

      {error ? (
        <p className="bg-destructive text-destructive-foreground border-foreground rounded-md border px-2 py-1 text-xs">
          {t(`adminDashboard.hostedAgents.evals.editorErrors.${error}`, {
            defaultValue: EXPECTATION_ERROR_DEFAULTS[error],
          })}
        </p>
      ) : null}
    </div>
  )
}

function CaseError({
  codes,
  match,
}: {
  codes: HostedAgentEvalCaseErrorCode[]
  match: HostedAgentEvalCaseErrorCode[]
}) {
  const { t } = useTranslation()
  const code = codes.find((candidate) => match.includes(candidate))
  if (!code) return null
  return (
    <p className="bg-destructive text-destructive-foreground border-foreground rounded-md border px-2 py-1 text-xs">
      {t(`adminDashboard.hostedAgents.evals.editorErrors.${code}`, {
        defaultValue: CASE_ERROR_DEFAULTS[code],
      })}
    </p>
  )
}

const KIND_HELP_DEFAULTS: Record<string, string> = {
  run_status: "Checks how the run itself ended, before looking at the answer.",
  answer_contains: "The answer must contain this text.",
  answer_not_contains: "The answer must not contain this text.",
  tool_called: "The agent must have called this tool successfully at least once.",
  tool_not_called: "The agent must not have called this tool.",
  tool_denied: "The tool call must have been refused — use this to prove a guardrail holds.",
  artifact_produced: "The run must have produced at least this many artifacts.",
  cited_source: "The answer must cite at least this many sources.",
  max_cost_sek: "The run must not cost more than this.",
  max_tokens: "The run must not use more tokens than this.",
  max_duration_ms: "The run must finish within this time.",
}

const LIMIT_LABEL_DEFAULTS: Record<string, string> = {
  max_cost_sek: "Maximum cost (SEK)",
  max_tokens: "Maximum tokens",
  max_duration_ms: "Maximum duration (ms)",
}

const CASE_ERROR_DEFAULTS: Record<HostedAgentEvalCaseErrorCode, string> = {
  name_required: "Give the case a name.",
  name_too_long: "The name is too long.",
  prompt_required: "Write the prompt the agent should receive.",
  prompt_too_long: "The prompt is too long.",
  timeout_invalid: "Enter a timeout between 1 and 300 seconds.",
  expectations_required: "Add at least one expectation.",
  expectations_too_many: "A case can hold at most 20 expectations.",
  expectation_invalid: "One or more expectations are incomplete.",
}

const EXPECTATION_ERROR_DEFAULTS: Record<HostedAgentEvalExpectationErrorCode, string> = {
  expected_required: "Choose the required run status.",
  value_required: "Enter the text to look for.",
  value_too_long: "The text is too long (max 500 characters).",
  tool_required: "Enter the tool name.",
  tool_too_long: "The tool name is too long (max 100 characters).",
  detail_too_long: "The argument text is too long (max 200 characters).",
  code_invalid: "That denial reason is not one the gateway can report.",
  min_count_invalid: "Enter a whole number between 1 and 100.",
  limit_required: "Enter the limit as a number of 0 or more.",
}
